import {
  Target,
  ChartLineUp,
  Brain,
  Broadcast,
  Funnel,
  UserCircleGear,
  TrendUp,
} from "@phosphor-icons/react";
import { subdomainImage } from "./utils";

const slug = "leadscoring";

/** LeadscoringBizlem — leadscoring.bizlem.com */
const leadscoringConfig = {
  slug,
  name: "LeadscoringBizlem",
  shortName: "Leadscoring",
  host: "leadscoring.bizlem.com",
  icon: Target,
  theme: {
    primary: "bg-blue-600",
    primaryHover: "hover:bg-blue-700",
    primaryText: "text-blue-600",
    primaryBorder: "border-blue-600",
    tint: "bg-blue-50",
    heroGradient: "from-blue-700 via-blue-600 to-blue-500",
    cream: "bg-blue-50/40",
    badge: "bg-blue-50 border-blue-200 text-blue-700",
    ring: "ring-blue-100",
  },
  hero: {
    badge: "AI-Powered Sales Intelligence",
    headline: ["Smarter Leads.", "Higher Conversions.", "Real Revenue."],
    highlightLine: 1,
    description:
      "Score, prioritize, and convert industrial leads with AI-driven intelligence — built for complex B2B sales cycles in heavy industry.",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    ctaPrimary: "Request Demo",
    ctaSecondary: "Explore Solutions",
  },
  about: {
    label: "WHAT WE DO",
    heading: "Turning pipeline data into predictable revenue",
    body: "LeadscoringBizlem helps industrial enterprises identify high-intent prospects, automate qualification, and align sales teams around accounts that convert.",
  },
  modules: {
    label: "MODULES",
    heading: "Six Integrated Modules. One Intelligent Pipeline.",
    body: "Every module works in sync — so marketing, sales, and leadership operate on a single source of truth for lead quality and conversion.",
    items: [
      { icon: Funnel, title: "Lead Capture & Enrichment", body: "Aggregate leads from web, events, and CRM with automatic firmographic enrichment.", iconBg: "bg-blue-100 text-blue-700" },
      { icon: ChartLineUp, title: "AI Lead Scoring", body: "Predict conversion probability using behavioral signals, firmographics, and engagement history.", iconBg: "bg-amber-100 text-amber-700" },
      { icon: UserCircleGear, title: "Sales Routing", body: "Route hot leads to the right rep instantly with territory and industry-aware assignment rules.", iconBg: "bg-emerald-100 text-emerald-700" },
      { icon: TrendUp, title: "Pipeline Analytics", body: "Real-time dashboards for conversion rates, velocity, and revenue attribution by segment.", iconBg: "bg-violet-100 text-violet-700" },
      { icon: Brain, title: "Intent Intelligence", body: "Detect buying signals across digital touchpoints and trigger timely outreach workflows.", iconBg: "bg-rose-100 text-rose-700" },
      { icon: Broadcast, title: "Revenue Command Center", body: "Unified view of pipeline health, forecast accuracy, and team performance.", iconBg: "bg-sky-100 text-sky-700" },
    ],
  },
  solutions: {
    label: "SOLUTIONS",
    heading: "Built for industrial sales teams",
    items: [
      {
        title: "Enterprise B2B",
        points: ["Multi-stakeholder deal scoring", "Account-based prioritization", "CRM & ERP integration", "Executive pipeline reporting"],
        image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      },
      {
        title: "Channel Partners",
        points: ["Partner lead distribution", "Co-selling workflow automation", "Deal registration intelligence", "Performance benchmarking"],
        image: subdomainImage(slug, "channel-partners.png"),
      },
      {
        title: "Inside Sales",
        points: ["Automated lead qualification", "Call prioritization queues", "Follow-up sequence triggers", "Conversion playbooks"],
        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      },
      {
        title: "Marketing Ops",
        points: ["Campaign ROI attribution", "MQL-to-SQL conversion tracking", "Audience segmentation", "Lead quality feedback loops"],
        image: subdomainImage(slug, "marketing-ops.png"),
      },
    ],
  },
  whyChoose: {
    label: "WHY CHOOSE US",
    heading: "Built for Scale. Designed for Simplicity.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    items: [
      "One unified platform for lead scoring and pipeline intelligence",
      "Scalable across industries and sales team sizes",
      "AI models trained on industrial B2B conversion patterns",
      "Seamless CRM and marketing automation integration",
    ],
  },
  footerTagline:
    "AI-Powered Lead Intelligence for Industrial Enterprises — turning complex sales cycles into predictable revenue.",
  contactIndustry: "Lead Scoring & Sales Intelligence",
};

export default leadscoringConfig;
